// Resolver de inputs visibles para una decision/BKM, dividido en
// conectados (existen como InputData/Parameter en el modelo) y huérfanos
// (referenciados desde la lógica pero sin fuente formal).
//
// Output:
//   {
//     connected: [{ name, typeRef, sourceKind, sourceId }],
//     orphans:   [{ name, typeRef, sources: ['decisionTable.col0', ...] }],
//   }

import { extractFreeIdents } from '../validation/rules';
import { sanitizeFeelIdent } from '../feel/ident';

const stripHref = (h) => (h || '').split('#').pop();

// Identificador FEEL "simple": un único token, sin operadores ni espacios.
const SIMPLE_IDENT_RE = /^[A-Za-z_][\w]*$/;

function isSimpleIdent(text) {
  if (!text) return false;
  return SIMPLE_IDENT_RE.test(String(text).trim());
}

// Devuelve [{ name, source, localTypeRef }] — identificadores libres
// detectados dentro de la lógica de un decision/BKM, con metadata para
// reporting/UI. Recursa por todos los expression types (Context entries,
// List elements, Relation cells, FunctionDefinition body, Invocation
// bindings y calledFunction) marcando el `source` con la ruta para que la
// UI pueda explicar dónde apareció el orphan.
function collectIdentsFromLogic(logic, baseSource) {
  if (!logic) return [];
  const out = [];

  const walk = (node, path, localBoundIdents) => {
    if (!node || !node.$type) return;
    const bound = localBoundIdents || new Set();
    const t = node.$type;

    if (t === 'dmn:DecisionTable') {
      (node.input || []).forEach((inp, idx) => {
        const text = inp.inputExpression && inp.inputExpression.text;
        const src = `${path}/col${idx}`;
        if (isSimpleIdent(text)) {
          out.push({
            name: String(text).trim(),
            source: src,
            localTypeRef: (inp.inputExpression && inp.inputExpression.typeRef) || null,
          });
        } else if (text) {
          extractFreeIdents(text).forEach((n) => {
            if (bound.has(n)) return;
            out.push({ name: n, source: src, localTypeRef: null });
          });
        }
      });
      return;
    }

    if (t === 'dmn:LiteralExpression') {
      extractFreeIdents(node.text || '').forEach((n) => {
        if (bound.has(n)) return;
        out.push({ name: n, source: path, localTypeRef: null });
      });
      return;
    }

    if (t === 'dmn:Context') {
      // Entries with names introduce locally-bound idents that subsequent
      // entries (and the result entry) can reference.
      const inner = new Set(bound);
      (node.contextEntry || []).forEach((entry, idx) => {
        const entryName = entry.variable && entry.variable.name;
        const childPath = entryName
          ? `${path}/${entryName}`
          : `${path}/entry${idx}`;
        if (entry.value) walk(entry.value, childPath, inner);
        if (entryName) inner.add(entryName);
      });
      return;
    }

    if (t === 'dmn:List') {
      (node.elements || []).forEach((child, idx) => {
        walk(child, `${path}/[${idx}]`, bound);
      });
      return;
    }

    if (t === 'dmn:Relation') {
      // Column names are local to the relation rows.
      const colNames = (node.column || []).map((c) => c && c.name).filter(Boolean);
      const rowBound = new Set(bound);
      colNames.forEach((n) => rowBound.add(n));
      (node.row || []).forEach((row, rIdx) => {
        (row.elements || []).forEach((cell, cIdx) => {
          walk(cell, `${path}/row${rIdx}/col${cIdx}`, rowBound);
        });
      });
      return;
    }

    if (t === 'dmn:Invocation') {
      if (node.calledFunction) walk(node.calledFunction, `${path}/calledFunction`, bound);
      (node.binding || []).forEach((b, idx) => {
        const pname = b.parameter && b.parameter.name;
        const bp = pname ? `${path}/binding(${pname})` : `${path}/binding${idx}`;
        if (b.bindingFormula) walk(b.bindingFormula, bp, bound);
      });
      return;
    }

    if (t === 'dmn:FunctionDefinition') {
      // Formal parameters are bound inside the body.
      const inner = new Set(bound);
      (node.formalParameter || []).forEach((p) => {
        if (p && p.name) inner.add(p.name);
      });
      if (node.body) walk(node.body, `${path}/body`, inner);
      return;
    }

    // Unknown / unsupported expression node: try to read .text as a fallback.
    if (typeof node.text === 'string') {
      extractFreeIdents(node.text).forEach((n) => {
        if (bound.has(n)) return;
        out.push({ name: n, source: path, localTypeRef: null });
      });
    }
  };

  walk(logic, baseSource || rootSourceForType(logic.$type), null);
  return out;
}

function rootSourceForType(t) {
  switch (t) {
    case 'dmn:DecisionTable':      return 'decisionTable';
    case 'dmn:LiteralExpression':  return 'literal';
    case 'dmn:Context':            return 'context';
    case 'dmn:List':               return 'list';
    case 'dmn:Invocation':         return 'invocation';
    case 'dmn:Relation':           return 'relation';
    case 'dmn:FunctionDefinition': return 'function';
    default:                       return 'logic';
  }
}

export function resolveDecisionInputs(definitions, element) {
  if (!definitions || !element) return { connected: [], orphans: [] };

  const byId = new Map();
  const byName = new Map();
  (definitions.drgElement || []).forEach((e) => {
    byId.set(e.id, e);
    if (e.$type !== 'dmn:InputData' && e.$type !== 'dmn:BusinessKnowledgeModel') return;
    // Index by both raw and sanitized name so a FEEL ident (single token, no
    // spaces) finds a source whose display name has spaces.
    if (e.name) byName.set(e.name, e);
    const ident = sanitizeFeelIdent(e.name || '');
    if (ident && !byName.has(ident)) byName.set(ident, e);
  });

  const connected = [];
  const seenConnected = new Set();
  const orphans = [];
  const seenOrphans = new Map(); // name -> { typeRef, sources }

  const pushConnected = (entry) => {
    if (seenConnected.has(entry.name)) return;
    seenConnected.add(entry.name);
    connected.push(entry);
  };

  const pushOrphan = (name, source, localTypeRef) => {
    const prev = seenOrphans.get(name);
    if (prev) {
      prev.sources.push(source);
      if (prev.typeRef == null && localTypeRef != null) prev.typeRef = localTypeRef;
    } else {
      seenOrphans.set(name, { typeRef: localTypeRef, sources: [source] });
    }
  };

  if (element.$type === 'dmn:Decision') {
    (element.informationRequirement || []).forEach((req) => {
      const id = stripHref(
        (req.requiredInput && req.requiredInput.href) ||
        (req.requiredDecision && req.requiredDecision.href)
      );
      const tgt = byId.get(id);
      if (!tgt) return;
      // Connected upstream is referenced from FEEL by an ident; if there's
      // no variable.name, fall back to the sanitized element name.
      const name = (tgt.variable && tgt.variable.name)
        || sanitizeFeelIdent(tgt.name)
        || tgt.id;
      pushConnected({
        name,
        typeRef: (tgt.variable && tgt.variable.typeRef) || null,
        sourceKind: tgt.$type.replace(/^dmn:/, ''),
        sourceId: tgt.id,
      });
    });

    (element.knowledgeRequirement || []).forEach((kr) => {
      const id = stripHref(kr.requiredKnowledge && kr.requiredKnowledge.href);
      const tgt = byId.get(id);
      if (!tgt) return;
      const name = sanitizeFeelIdent(tgt.name) || tgt.id;
      pushConnected({
        name,
        typeRef: (tgt.variable && tgt.variable.typeRef) || null,
        sourceKind: 'BusinessKnowledgeModel',
        sourceId: tgt.id,
      });
    });

    const idents = collectIdentsFromLogic(element.decisionLogic);
    idents.forEach((it) => {
      if (seenConnected.has(it.name)) return;
      const matched = byName.get(it.name);
      if (matched) {
        pushConnected({
          name: it.name,
          typeRef: (matched.variable && matched.variable.typeRef) || null,
          sourceKind: matched.$type.replace(/^dmn:/, ''),
          sourceId: matched.id,
        });
        return;
      }
      pushOrphan(it.name, it.source, it.localTypeRef);
    });
  } else if (element.$type === 'dmn:BusinessKnowledgeModel') {
    const params = (element.encapsulatedLogic && element.encapsulatedLogic.formalParameter) || [];
    params.forEach((p) => {
      const name = p.name || '';
      if (!name) return;
      pushConnected({
        name,
        typeRef: p.typeRef || null,
        sourceKind: 'Parameter',
        sourceId: p.id || null,
      });
    });

    (element.knowledgeRequirement || []).forEach((kr) => {
      const id = stripHref(kr.requiredKnowledge && kr.requiredKnowledge.href);
      const tgt = byId.get(id);
      if (!tgt) return;
      const name = sanitizeFeelIdent(tgt.name) || tgt.id;
      pushConnected({
        name,
        typeRef: (tgt.variable && tgt.variable.typeRef) || null,
        sourceKind: 'BusinessKnowledgeModel',
        sourceId: tgt.id,
      });
    });

    const idents = collectIdentsFromLogic(element.encapsulatedLogic);
    idents.forEach((it) => {
      if (seenConnected.has(it.name)) return;
      const matched = byName.get(it.name);
      if (matched && matched.$type === 'dmn:BusinessKnowledgeModel') {
        pushConnected({
          name: it.name,
          typeRef: (matched.variable && matched.variable.typeRef) || null,
          sourceKind: 'BusinessKnowledgeModel',
          sourceId: matched.id,
        });
        return;
      }
      pushOrphan(it.name, it.source, it.localTypeRef);
    });
  } else {
    return { connected: [], orphans: [] };
  }

  seenOrphans.forEach((v, name) => {
    orphans.push({ name, typeRef: v.typeRef, sources: v.sources });
  });

  return { connected, orphans };
}
